import { useCallback, useEffect, useRef, useState } from 'react';
import { refreshSession } from './api';

export type ConnectionState = 'connecting' | 'live' | 'reconnecting' | 'offline';

export interface RealtimeFrame<T = unknown> {
  type: string;
  eventId: string;
  traceId?: string;
  payload: T;
  receivedAt: number;
}

/**
 * Subscribes to the realtime gateway. Authenticates with the access token in the first frame (never in the
 * URL) and reconnects with backoff; a 4401 close (expired token) refreshes the session first.
 * `send` pushes client frames (presence) when connected; it is a no-op while reconnecting.
 */
export function useRealtime(token: string | undefined, onFrame: (f: RealtimeFrame) => void) {
  const [state, setState] = useState<ConnectionState>('connecting');
  const handler = useRef(onFrame);
  handler.current = onFrame;
  const socket = useRef<WebSocket | null>(null);

  useEffect(() => {
    if (!token) return;
    let attempt = 0;
    let closed = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let current = token;

    const connect = () => {
      const proto = location.protocol === 'https:' ? 'wss' : 'ws';
      const ws = new WebSocket(`${proto}://${location.host}/ws`);
      socket.current = ws;
      ws.onopen = () => ws.send(JSON.stringify({ type: 'auth', token: current }));
      ws.onmessage = (e) => {
        const f = JSON.parse(String(e.data));
        if (f.type === 'ready') {
          attempt = 0;
          setState('live');
          return;
        }
        handler.current({ ...f, receivedAt: Date.now() });
      };
      ws.onclose = async (e) => {
        // A stale socket's late close must not clear the reference to its replacement.
        if (socket.current === ws) socket.current = null;
        if (closed) return;
        setState(attempt > 3 ? 'offline' : 'reconnecting');
        if (e.code === 4401) current = (await refreshSession())?.accessToken ?? current;
        attempt++;
        timer = setTimeout(connect, Math.min(15_000, 500 * 2 ** attempt) * (0.5 + Math.random() / 2));
      };
    };
    connect();
    return () => {
      closed = true;
      clearTimeout(timer);
      socket.current?.close();
    };
  }, [token]);

  const send = useCallback((frame: unknown) => {
    const ws = socket.current;
    if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify(frame));
  }, []);

  return { state, send };
}
